/**
 * The hosted embed's session token, asked of the page that frames it.
 *
 * An organization that sets `require_session_token` has every read of its hosted embed carry a
 * session token, the map's tiles included, and a token lives minutes. The embed cannot mint
 * one: minting takes the organization's secret key, which only the integrator's server holds. So
 * the embed asks the page that frames it, and that page asks the server: the snippet's script
 * answers each request from the integrator's own endpoint.
 *
 * {@link askFramingPageForSession} is one such ask, shaped as the `mint` a `SessionTokenEngine`
 * of `@mountaya/client` takes, so the engine schedules the next one a minute before the answer's
 * `expires_at` and retries a page that did not answer:
 *
 * ```ts
 * import { SessionTokenEngine } from "@mountaya/client";
 * import { askFramingPageForSession } from "@mountaya/studio/engines";
 * import { systemClock } from "@mountaya/studio/seams";
 *
 * const session = new SessionTokenEngine({
 *   mint: () => askFramingPageForSession({ clock: systemClock(), embed: window }),
 * });
 * ```
 */

import type { RestSchema } from "@mountaya/client";
import { MountayaElementsError } from "@mountaya/elements/errors";

import { type Clock, MAX_TIMER_MS } from "../../seams/rules/clock.js";
import {
  REQUEST_SESSION_TOKEN_MESSAGE,
  setSessionTokenMessageSchema,
} from "../rules/bridgeMessages.js";

/** FRAMING_PAGE_SESSION_TIMEOUT_MS is how long an ask waits for the framing page by default. */
export const FRAMING_PAGE_SESSION_TIMEOUT_MS = 10_000;

/**
 * FramingPageMessage is the part of a window `message` event an ask reads: the payload, and the
 * window that posted it.
 */
export interface FramingPageMessage {
  readonly data: unknown;
  readonly source: unknown;
}

/**
 * FramingPageWindow is the part of the embed's own window an ask uses: its parent, the page that
 * frames it, and the `message` listener the answer arrives through. A browser `Window` is one.
 */
export interface FramingPageWindow {
  readonly addEventListener: (
    type: "message",
    listener: (event: FramingPageMessage) => void,
  ) => void;
  readonly parent: {
    readonly postMessage: (message: unknown, targetOrigin: string) => void;
  };
  readonly removeEventListener: (
    type: "message",
    listener: (event: FramingPageMessage) => void,
  ) => void;
}

/** AskFramingPageForSessionOptions is what {@link askFramingPageForSession} takes. */
export interface AskFramingPageForSessionOptions {
  /**
   * Where the wait's time comes from: `systemClock()` of `@mountaya/studio/seams` in a page, which
   * the host chooses as it chooses every seam.
   */
  readonly clock: Clock;
  /**
   * How long to wait for the framing page's answer, in milliseconds, before the ask rejects. A
   * whole or fractional number from 1 to 2,147,483,647, the longest delay a timer honours.
   *
   * @default 10000
   */
  readonly timeoutMs?: number | undefined;
  /**
   * The embed's own window, whose parent is the page that frames it. Handed in rather than read
   * off the global, so a host decides which window asks, and a suite hands it a stand-in.
   */
  readonly embed: FramingPageWindow;
}

/**
 * timeoutFrom reads the wait an ask is given, refusing one no timer honours: a `NaN`, an infinity,
 * zero or less, or one past the longest delay a timer keeps.
 */
const timeoutFrom = (timeoutMs: number | undefined): number => {
  if (timeoutMs === undefined) {
    return FRAMING_PAGE_SESSION_TIMEOUT_MS;
  }

  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0 || timeoutMs > MAX_TIMER_MS) {
    throw new MountayaElementsError(
      `askFramingPageForSession: timeoutMs is ${String(timeoutMs)}; pass a number of ` +
        `milliseconds from 1 to ${MAX_TIMER_MS}, or omit it for ${FRAMING_PAGE_SESSION_TIMEOUT_MS}.`,
    );
  }

  return timeoutMs;
};

/**
 * askFramingPageForSession asks the page that frames the embed for a session token, and resolves
 * with the first valid `mountaya:setSessionToken` that page posts back.
 *
 * Only the framing page is heard: a message from any other window (a frame beside the embed, a
 * popup, a frame inside it) is ignored, as is one that does not parse, so a script the framing
 * page did not run itself never hands the embed a token. The answer resolves as a `Session` whose
 * `user_id` is null: the hosted embed reads what is published and acts as no member.
 *
 * @param options - The embed's own window, and how long to wait.
 * @returns The session, as `POST /v1/sessions` answers one.
 * @throws {MountayaElementsError} Where `timeoutMs` is no delay a timer honours. The promise
 *   rejects with one where the embed is not framed, since there is no page to ask, and where the
 *   framing page has not answered within `timeoutMs`; the listener is removed either way, so an
 *   unanswered ask holds nothing.
 */
export const askFramingPageForSession = (
  options: AskFramingPageForSessionOptions,
): Promise<RestSchema.Session> => {
  const timeoutMs = timeoutFrom(options.timeoutMs);
  const { clock, embed } = options;

  return new Promise((resolve, reject) => {
    // A window that is its own parent is a top-level page: the request would reach only itself.
    if (Object.is(embed.parent, embed)) {
      reject(
        new MountayaElementsError(
          "askFramingPageForSession: the embed is not framed, so no page can hand it a session token.",
        ),
      );

      return;
    }

    let cancel = (): void => {};
    const listener = (event: FramingPageMessage): void => {
      if (event.source !== embed.parent) {
        return;
      }

      const parsed = setSessionTokenMessageSchema.safeParse(event.data);
      if (!parsed.success) {
        return;
      }

      cancel();
      embed.removeEventListener("message", listener);
      resolve({
        expires_at: new Date(parsed.data.expiresAt),
        token: parsed.data.token,
        user_id: null,
      });
    };

    embed.addEventListener("message", listener);
    cancel = clock.after(timeoutMs, () => {
      embed.removeEventListener("message", listener);
      reject(
        new MountayaElementsError(
          `askFramingPageForSession: the framing page answered no session token within ${timeoutMs} ms.`,
        ),
      );
    });
    embed.parent.postMessage({ type: REQUEST_SESSION_TOKEN_MESSAGE }, "*");
  });
};
